// src/components/BugReportButton.jsx
import React, { useState } from 'react';
import { Bug, X, AlertCircle, CheckCircle, Loader } from 'lucide-react';
import BugReportService from '../services/BugReportService';

const BugReportButton = ({ restaurantId, restaurantName }) => {
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'Medium',
    category: 'General'
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);
      await BugReportService.submitBugReport(restaurantId, restaurantName, formData);
      
      setSuccess(true);
      setTimeout(() => {
        setShowModal(false);
        setSuccess(false);
        setFormData({
          title: '',
          description: '',
          priority: 'Medium',
          category: 'General'
        });
      }, 2000);
    } catch (error) {
      console.error('Error submitting bug report:', error);
      alert('Failed to submit bug report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <>
      {/* Floating Bug Report Button */}
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 left-6 bg-red-500 text-white p-4 rounded-full shadow-lg hover:bg-red-600 transition-all hover:scale-110 z-40 flex items-center gap-2 group"
        title="Report a Bug"
      >
        <Bug className="w-6 h-6" />
        <span className="hidden group-hover:inline-block text-sm font-medium whitespace-nowrap">
          Report Bug
        </span>
      </button>

      {/* Bug Report Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <div className="flex items-center gap-3">
                <div className="bg-red-100 p-2 rounded-lg">
                  <Bug className="w-6 h-6 text-red-600" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-800">Report a Bug</h2>
                  <p className="text-sm text-gray-600">Help us improve your experience</p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 p-1"
                disabled={submitting}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Success Message */}
            {success && (
              <div className="m-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-green-800 font-semibold">Bug Report Submitted!</p>
                  <p className="text-green-700 text-sm">Thank you for helping us improve. We'll review your report shortly.</p>
                </div>
              </div>
            )}

            {/* Form */}
            {!success && (
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                {/* Info Banner */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-semibold mb-1">Your feedback matters!</p>
                    <p>Please provide as much detail as possible to help us resolve the issue quickly.</p>
                  </div>
                </div>

                {/* Bug Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bug Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleChange('title', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="Brief description of the issue"
                    required
                    disabled={submitting}
                    maxLength={100}
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.title.length}/100 characters</p>
                </div>

                {/* Category and Priority */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.category}
                      onChange={(e) => handleChange('category', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                      disabled={submitting}
                    >
                      <option value="General">General</option>
                      <option value="Menu">Menu Management</option>
                      <option value="Orders">Orders</option>
                      <option value="QR Tables">QR Tables</option>
                      <option value="Dashboard">Dashboard</option>
                      <option value="Settings">Settings</option>
                      <option value="Performance">Performance</option>
                      <option value="UI/UX">UI/UX Issue</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.priority}
                      onChange={(e) => handleChange('priority', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                      disabled={submitting}
                    >
                      <option value="Low">Low - Minor inconvenience</option>
                      <option value="Medium">Medium - Affects workflow</option>
                      <option value="High">High - Critical issue</option>
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Detailed Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    rows="6"
                    placeholder="Please describe the bug in detail:
• What were you trying to do?
• What happened instead?
• What did you expect to happen?
• Steps to reproduce the issue"
                    required
                    disabled={submitting}
                    maxLength={1000}
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.description.length}/1000 characters</p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-red-500 text-white py-3 rounded-lg font-semibold hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition"
                >
                  {submitting ? (
                    <>
                      <Loader className="w-5 h-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Bug className="w-5 h-5" />
                      Submit Bug Report
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default BugReportButton;